/**
 * PaymentMethod Modal Component - Using FormModal with Auto-Save
 */
import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { FormModal } from '../../components/ui/FormModal';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { CreditCard, Banknote, QrCode, Wallet } from 'lucide-react';

const iconOptions = [
  { value: 'Banknote', label: 'Dinheiro', icon: Banknote },
  { value: 'CreditCard', label: 'Cartão', icon: CreditCard },
  { value: 'QrCode', label: 'PIX / QR', icon: QrCode },
  { value: 'Wallet', label: 'Carteira', icon: Wallet },
];

const paymentMethodSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório').max(50, 'Nome muito longo'),
  icon: z.string().min(1, 'Ícone é obrigatório'),
  is_active: z.boolean().optional(),
});

export function PaymentMethodModal({ open, onOpenChange, paymentMethod, onSave, loading }) {
  const { t } = useTranslation();
  const isEditing = !!paymentMethod?.id;

  const { register, handleSubmit, control, reset, watch, formState: { errors, isDirty } } = useForm({
    resolver: zodResolver(paymentMethodSchema),
    defaultValues: {
      name: '',
      icon: 'CreditCard',
      is_active: true,
    },
  });

  // Reset form when modal opens or paymentMethod changes
  useEffect(() => {
    if (!open) return; // Only reset when modal is opening
    
    if (paymentMethod) {
      reset({
        name: paymentMethod.name || '',
        icon: paymentMethod.icon || 'CreditCard',
        is_active: paymentMethod.is_active ?? true,
      });
    } else {
      reset({
        name: '',
        icon: 'CreditCard',
        is_active: true,
      });
    }
  }, [open, paymentMethod, reset]);

  const formValues = watch();

  const handleLoadDraft = (draft) => {
    reset(draft);
  };

  const onSubmit = (data) => {
    onSave(data);
  };

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title={isEditing ? 'Editar Forma de Pagamento' : 'Nova Forma de Pagamento'}
      description={isEditing ? 'Atualize os dados' : 'Preencha os dados'}
      size="sm"
      formId="payment-method"
      entityId={paymentMethod?.id}
      isDirty={isDirty}
      formValues={formValues}
      onLoadDraft={handleLoadDraft}
      onSave={handleSubmit(onSubmit)}
      loading={loading}
      confirmText={t('common.save')}
      cancelText={t('common.cancel')}
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name">Nome *</Label>
          <Input
            id="name"
            {...register('name')}
            placeholder="Ex: Cartão de Crédito"
            data-testid="payment-method-name-input"
          />
          {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="icon">Ícone</Label>
          <Controller
            name="icon"
            control={control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {iconOptions.map(opt => {
                    const Icon = opt.icon;
                    return (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4" />
                          {opt.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <Controller
          name="is_active"
          control={control}
          render={({ field }) => (
            <div className="flex items-center gap-2">
              <Switch
                id="is_active"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
              <Label htmlFor="is_active">{t('common.active')}</Label>
            </div>
          )}
        />
      </div>
    </FormModal>
  );
}

export default PaymentMethodModal;
